/**
 * Shared helpers for the ConfigurationSelector ("Choose your layout") surface.
 */

/**
 * Intrinsic px used for the `width`/`height` HTML attributes on configuration/swatch/product
 * thumbnails (KINDW-1833). These images are square Cylindo/PIM renders, so a 1:1 value gives the
 * browser a correct aspect ratio to reserve layout space (reduces CLS) without affecting the
 * CSS-controlled display size. The exact number is irrelevant — only the 1:1 ratio matters.
 */
export const PIM_SQUARE_THUMBNAIL_PX = 400;

/**
 * Intrinsic px for the `width`/`height` HTML attributes on the View details configuration /
 * package thumbnails (KINDW-1833). Unlike the square swatches, these are LANDSCAPE PIM
 * configuration images (`c_pimlyConfigurationImage`), rendered ~3:2 in the thumbnail swiper.
 * Providing the attributes lets the browser reserve correctly-proportioned layout space before
 * the image loads (reduces CLS). Only the ratio matters — display size stays CSS-controlled.
 */
export const PIM_CONFIG_THUMBNAIL_WIDTH_PX = 600;
export const PIM_CONFIG_THUMBNAIL_HEIGHT_PX = 400;

/**
 * Resolve a configuration thumbnail URL for a product (or search hit).
 *
 * Complex masters / predefined-set masters always carry
 * c_pimlyConfigurationImageSwatch (see SFRA `product.actions.js`, which falls
 * back to the first c_pimlyConfigurationImage entry).
 *
 * Variation-base ("Freeform", KINDW-1121) simple masters often carry NEITHER
 * PIM-configuration attribute, so the trigger card / panel footer / list tile
 * would otherwise render an empty Skeleton forever. Fall back to:
 *   1. c_pimlyConfigurationImageSwatch
 *   2. first c_pimlyConfigurationImage entry
 *   3. first PIM image group's first image (disBaseLink → link)
 *   4. product.image (disBaseLink → link)
 *
 * @param {object} product - SFRA product / search hit.
 * @returns {string|null} Resolved image URL, or null if none found.
 */
export const resolveProductThumbnailUrl = (product) => {
  if (!product) return null;

  const firstImageGroup = product.imageGroups?.find(
    (g) => Array.isArray(g.images) && g.images.length > 0
  );
  const firstImage = firstImageGroup?.images?.[0];

  return (
    product.c_pimlyConfigurationImageSwatch ||
    (Array.isArray(product.c_pimlyConfigurationImage) && product.c_pimlyConfigurationImage[0]) ||
    firstImage?.disBaseLink ||
    firstImage?.link ||
    product.image?.disBaseLink ||
    product.image?.link ||
    null
  );
};

/**
 * Resolve a swatch thumbnail for the "Choose from popular sets" panel rows — the linked
 * predefined-set product, the cover variant, AND every recommended product option.
 *
 * Cover variants (`product.type?.variant === true`): same priority as
 * {@link resolveProductThumbnailUrl} — swatch → first config image → image fallbacks.
 *
 * All other products: first `c_pimlyConfigurationImage` entry only (no swatch attr), then:
 *   1. first c_pimlyConfigurationImage entry
 *   2. first PIM image group's first image (disBaseLink → link)
 *   3. product.image (disBaseLink → link)
 *
 * @param {object} product - SFRA product / variant / search hit.
 * @returns {string|null} Resolved image URL, or null if none found.
 */
export const resolveSwatchThumbnailUrl = (product) => {
  if (!product) return null;

  if (product.type?.variant === true) {
    return resolveProductThumbnailUrl(product);
  }

  const firstImageGroup = product.imageGroups?.find(
    (g) => Array.isArray(g.images) && g.images.length > 0
  );
  const firstImage = firstImageGroup?.images?.[0];

  return (
    (Array.isArray(product.c_pimlyConfigurationImage) && product.c_pimlyConfigurationImage[0]) ||
    firstImage?.disBaseLink ||
    firstImage?.link ||
    product.image?.disBaseLink ||
    product.image?.link ||
    null
  );
};
